import Button from "../../components/Button";
import { CubeIcon } from "@heroicons/react/24/outline";
import useOrganizationProjects from "./hooks/useOrganizationProjects";
import useNav from "../../hooks/useNavigation";
import ProjectModal from "./components/ProjectModal";
import { useMemo, useState } from "react";
import FullLoading from "../../components/FullLoading";
import { Project, ProjectType } from "../../__generated__/graphql";
import { formatDate, projectTypeToName } from "../../lib/utils";
import { ArrowDownTrayIcon } from "@heroicons/react/24/solid";
import useOrganizationHasIntegrations from "../../hooks/useOrganizationHasIntegrations";
import PimsImportModal from "./components/PimsImportModal";
import { FaPlus } from "react-icons/fa6";
import SectionEmptyState from "../../components/SectionEmptyState";

export default function Projects() {
  const navigateWithLog = useNav();
  const { hasPimsIntegration } = useOrganizationHasIntegrations();

  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] =
    useState(false);
  const [isImportProjectsModalOpen, setIsImportProjectsModalOpen] =
    useState(false);

  const { data, loading } = useOrganizationProjects();

  const hasProjects = useMemo(() => {
    return (data?.projects?.length ?? 0) > 0;
  }, [data?.projects]);

  const subtitle = () => {
    return (
      <div>
        Un <span className="text-accent font-medium">Projet</span> est un
        ensemble cohérent d'événements, de documents et de participants réunis
        autour d'un même objectif (tournée, film, album, série de concerts,
        etc.).
      </div>
    );
  };

  const projectComponent = (project: Project) => {
    return (
      <div
        key={project.id}
        onClick={() => navigateWithLog(`/projects/${project.id}`)}
        className="bg-card rounded-md p-4 w-md h-32 border border-border hover:bg-card/80 cursor-pointer flex flex-col gap-2 justify-between"
      >
        <div className="flex flex-col">
          <div className="text-xs text-accent">
            {project.projectType === ProjectType.Other
              ? project.otherProjectType
              : projectTypeToName[project.projectType]}
          </div>

          <div className="font-bold text-primary text-lg">{project.name}</div>
        </div>

        <div className="flex flex-row justify-between items-center">
          <div className="text-xs text-secondary">
            Créé le {formatDate(project.createdAt)}
          </div>
        </div>
      </div>
    );
  };

  const pageHeader = () => {
    return (
      <div>
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-primary">Projets</h1>

          <div className="flex gap-2">
            {hasPimsIntegration ? (
              <Button
                variant="secondary"
                onClick={() => setIsImportProjectsModalOpen(true)}
              >
                <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                Importer des projets
              </Button>
            ) : null}

            <Button
              variant="secondary"
              onClick={() => setIsCreateProjectModalOpen(true)}
              dataTestId="create-project-button"
            >
              <FaPlus className="w-4 h-4 mr-2" />
              Créer un projet
            </Button>
          </div>
        </div>

        <div className="text-secondary text-sm mt-4">{subtitle()}</div>
      </div>
    );
  };

  const emptyState = () => {
    return (
      <SectionEmptyState
        icon={<CubeIcon className="w-10 h-10 text-accent" />}
        title="Projets"
        description={subtitle()}
        actionButton={
          <Button onClick={() => setIsCreateProjectModalOpen(true)}>
            <FaPlus className="w-4 h-4 mr-2" />
            Créer un projet
          </Button>
        }
      />
    );
  };

  const pageContent = () => {
    if (!hasProjects) {
      return emptyState();
    }

    return (
      <div className="flex flex-wrap gap-4">
        {data?.projects.map((project) => projectComponent(project))}
      </div>
    );
  };

  if (loading) {
    return <FullLoading />;
  }

  return (
    <div data-testid="projects-page">
      <PimsImportModal
        isOpen={isImportProjectsModalOpen}
        onClose={() => setIsImportProjectsModalOpen(false)}
      />

      <ProjectModal
        isOpen={isCreateProjectModalOpen}
        onClose={() => setIsCreateProjectModalOpen(false)}
      />

      {hasProjects ? pageHeader() : null}

      <div className="mt-8">{pageContent()}</div>
    </div>
  );
}
